import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import styled, { useTheme } from 'styled-components';

const getSize = (size, theme) => {
  if (size === 'small') {
    return theme.circularProgressIndicator.size.small;
  }
  if (size === 'base') {
    return theme.circularProgressIndicator.size.base;
  }
  return size;
};
const VIEWBOX_WIDTH = 125;
const VIEWBOX_HEIGHT = 125;
const VIEWBOX_HEIGHT_HALF = 62.5;
const VIEWBOX_CENTER_X = 62.5;
const VIEWBOX_CENTER_Y = 62.5;
const FILTER_ID = 'trail_filter';
const getStrokeWidth = ({
  strokeWidth,
  size,
  cpiTheme
}) => {
  if (typeof strokeWidth === 'number') {
    return strokeWidth;
  }
  const sizeValue = cpiTheme.size[size];
  if (sizeValue) {
    return parseInt(sizeValue.replace('px', ''), 10) * 0.18 / 2;
  }
  // v3 default value
  return 9;
};
const StyledSvg = /*#__PURE__*/styled.svg.withConfig({
  componentId: "sc-storm-ui-40013943__sc-1q06c4n-0"
})(["-webkit-appearance:none;-moz-appearance:none;appearance:none;position:absolute;width:", ";height:", ";"], ({
  $size,
  theme
}) => getSize($size, theme), ({
  $size,
  theme
}) => getSize($size, theme));

// copied from https://github.com/kevinsqi/react-circular-progressbar/blob/master/src/Path.tsx
// SVG path description specifies how the path should be drawn
function getPathDescription({
  pathRadius,
  counterClockwise
}) {
  const radius = pathRadius;
  const rotation = counterClockwise ? 1 : 0;

  // Move to center of canvas
  // Relative move to top canvas
  // Relative arc to bottom of canvas
  // Relative arc to top of canvas
  return `
      M ${VIEWBOX_CENTER_X},${VIEWBOX_CENTER_Y}
      m 0,-${radius}
      a ${radius},${radius} ${rotation} 1 1 0,${2 * radius}
      a ${radius},${radius} ${rotation} 1 1 0,-${2 * radius}
    `;
}
function getDashStyle({
  counterClockwise,
  dashRatio,
  pathRadius
}) {
  const diameter = Math.PI * 2 * pathRadius;
  const gapLength = (1 - dashRatio) * diameter;
  return {
    // Have dash be full diameter, and gap be full diameter
    strokeDasharray: `${diameter}px ${diameter}px`,
    // Shift dash backward by gapLength, so gap starts appearing at correct distance
    strokeDashoffset: `${counterClockwise ? -gapLength : gapLength}px`
  };
}
const PathComponent = ({
  strokeWidth,
  strokeLinecap,
  counterClockwise = false,
  dashRatio = 1,
  ...restProps
}) => {
  const pathRadius = VIEWBOX_HEIGHT_HALF - strokeWidth / 2;
  return /*#__PURE__*/React__default.createElement("path", _extends({}, restProps, getDashStyle({
    pathRadius,
    dashRatio,
    counterClockwise
  }), {
    strokeLinecap: strokeLinecap,
    strokeWidth: strokeWidth,
    fillOpacity: 0,
    d: getPathDescription({
      pathRadius,
      counterClockwise
    })
  }));
};
const Trail = /*#__PURE__*/styled(PathComponent).withConfig({
  componentId: "sc-storm-ui-40013943__sc-1q06c4n-1"
})(["stroke:", ";"], ({
  theme,
  $color
}) => $color !== null && $color !== void 0 ? $color : theme.progressIndicator.trackColor);
const Path = /*#__PURE__*/styled(PathComponent).withConfig({
  componentId: "sc-storm-ui-40013943__sc-1q06c4n-2"
})(["stroke:", ";"], ({
  theme,
  $color
}) => $color !== null && $color !== void 0 ? $color : theme.progressIndicator.valueColor.default[100]);
const CircularProgressBar = ({
  size,
  value,
  maxValue,
  minValue,
  strokeWidth: externalStrokeWidth,
  pathColor,
  trailColor,
  strokeLinecap
}) => {
  const pathRatio = (value - minValue) / (maxValue - minValue);
  const cpiTheme = useTheme().circularProgressIndicator;
  const strokeWidth = getStrokeWidth({
    strokeWidth: externalStrokeWidth,
    size,
    cpiTheme
  });
  return /*#__PURE__*/React__default.createElement(StyledSvg, {
    $size: size,
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: `0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`,
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("g", {
    filter: `url(#${FILTER_ID})`
  }, /*#__PURE__*/React__default.createElement(Trail, {
    strokeWidth: strokeWidth,
    strokeLinecap: "butt",
    $color: trailColor
  })), /*#__PURE__*/React__default.createElement(Path, {
    dashRatio: pathRatio,
    strokeWidth: strokeWidth,
    strokeLinecap: strokeLinecap,
    $color: pathColor
  }), /*#__PURE__*/React__default.createElement("defs", null, /*#__PURE__*/React__default.createElement("filter", {
    id: FILTER_ID,
    x: "0",
    y: "0",
    width: VIEWBOX_WIDTH,
    height: `${VIEWBOX_WIDTH + 1}`,
    filterUnits: "userSpaceOnUse",
    colorInterpolationFilters: "sRGB"
  }, /*#__PURE__*/React__default.createElement("feFlood", {
    floodOpacity: "0",
    result: "background"
  }), /*#__PURE__*/React__default.createElement("feBlend", {
    mode: "normal",
    in: "SourceGraphic",
    in2: "background",
    result: "shape"
  }), /*#__PURE__*/React__default.createElement("feColorMatrix", {
    in: "SourceAlpha",
    type: "matrix",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0",
    result: "hardAlpha"
  }), /*#__PURE__*/React__default.createElement("feMorphology", {
    radius: "1",
    operator: "dilate",
    in: "SourceAlpha",
    result: "innerShadow"
  }), /*#__PURE__*/React__default.createElement("feOffset", {
    dy: "1"
  }), /*#__PURE__*/React__default.createElement("feGaussianBlur", {
    stdDeviation: "2"
  }), /*#__PURE__*/React__default.createElement("feComposite", {
    in2: "hardAlpha",
    operator: "arithmetic",
    k2: "-1",
    k3: "1"
  }), /*#__PURE__*/React__default.createElement("feColorMatrix", {
    type: "matrix",
    values: "0 0 0 0 0.67451 0 0 0 0 0.686275 0 0 0 0 0.690196 0 0 0 1 0"
  }), /*#__PURE__*/React__default.createElement("feBlend", {
    mode: "normal",
    in2: "shape",
    result: "innerShadow"
  }))));
};

export { CircularProgressBar as C, getSize as g };
//# sourceMappingURL=CircularProgressBar-BFQh18L3.js.map
